import type { PostalAddress } from '../geolocation';

/** Fixed platform-level code emitted once per `create` audit row by `buildHistory` — never taken from a resource's `HistoryEventDefinition[]`. */
export const HISTORY_CREATED_EVENT_CODE = 'created';

/** Every discriminant `HistoryValue` variants can carry — single source of truth, never restated as raw literals. */
export const HISTORY_VALUE_KIND = {
  ACTOR_REF: 'actorRef',
  ADDRESS: 'address',
  AMOUNT: 'amount',
  LIST_DIFF: 'listDiff',
  MEASUREMENT: 'measurement',
  SCALAR: 'scalar',
  STATUS: 'status',
  TAGS: 'tags',
  TIMESLOT: 'timeslot',
} as const;
export type HistoryValueKind = (typeof HISTORY_VALUE_KIND)[keyof typeof HISTORY_VALUE_KIND];

export interface HistorySystemActor {
  kind: 'system';
}

export interface HistoryUserActor {
  id: string;
  kind: 'user';
  name?: string;
}

/** Who performed the action a `HistoryEntry` describes. A `system` actor never carries `id`/`name` — that state is unrepresentable. */
export type HistoryActor = HistorySystemActor | HistoryUserActor;

export interface HistoryActorRefValue {
  id: string;
  kind: typeof HISTORY_VALUE_KIND.ACTOR_REF;
  name: string;
}

export interface HistoryAddressValue {
  address: PostalAddress;
  kind: typeof HISTORY_VALUE_KIND.ADDRESS;
}

export interface HistoryAmountValue {
  currency: string;
  kind: typeof HISTORY_VALUE_KIND.AMOUNT;
  value: number;
}

export interface HistoryMeasurementValue {
  kind: typeof HISTORY_VALUE_KIND.MEASUREMENT;
  unit: string;
  value: number;
}

export interface HistoryListDiffValue {
  added: unknown[];
  kind: typeof HISTORY_VALUE_KIND.LIST_DIFF;
  removed: unknown[];
  updated: unknown[];
}

export interface HistoryScalarValue {
  kind: typeof HISTORY_VALUE_KIND.SCALAR;
  value: number | string;
}

export interface HistoryStatusValue {
  kind: typeof HISTORY_VALUE_KIND.STATUS;
  message?: string;
  reason?: string;
  status: string;
}

export interface HistoryTagsValue {
  added: string[];
  kind: typeof HISTORY_VALUE_KIND.TAGS;
  removed: string[];
}

export interface HistoryTimeslotValue {
  end: string;
  kind: typeof HISTORY_VALUE_KIND.TIMESLOT;
  start: string;
}

export type HistoryValue =
  | HistoryActorRefValue
  | HistoryAddressValue
  | HistoryAmountValue
  | HistoryListDiffValue
  | HistoryMeasurementValue
  | HistoryScalarValue
  | HistoryStatusValue
  | HistoryTagsValue
  | HistoryTimeslotValue;

/** One curated, resource-agnostic event derived from an audit trail — see `buildHistory` in `pharos-lib-back-utils`. */
export interface HistoryEntry {
  actor: HistoryActor;
  code: string;
  newValue: HistoryValue | null;
  previousValue: HistoryValue | null;
  timestamp: string;
}
